import { ApiError } from '../api.ts';
import type {
  AdminUser, Analysis, AuditEntry, EngagementListItem, Lob,
  RosterInputRow, RosterValidation, ProvisionResult, RespondentMonitoring, SurveyConfig,
  CustomQuestionsResponse, LibraryQuestionsResponse, LibraryAnchor,
} from './types.ts';

export { ApiError };

// Same fetch/ApiError contract as the survey client: JSON in/out, cookie session,
// non-2xx → thrown ApiError carrying status + server message/code.
async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: body !== undefined ? { 'content-type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const payload = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new ApiError(res.status, payload.message ?? payload.error ?? res.statusText, payload.error);
  }
  if (res.headers.get('content-type')?.includes('application/json')) return (await res.json()) as T;
  return undefined as T;
}

export const adminApi = {
  login: (email: string, password: string) =>
    request<{ ok: true }>('POST', '/api/admin/login', { email, password }),
  me: () => request<AdminUser>('GET', '/api/admin/me'),
  logout: () => request<{ ok: true }>('POST', '/api/admin/logout'),
  // Backend wraps list responses in a named key; unwrap to the array the UI expects.
  engagements: () =>
    request<{ engagements: EngagementListItem[] }>('GET', '/api/admin/engagements').then((r) => r.engagements),
  analysis: (engagementId: string, lobId?: string | null) => {
    const query = lobId ? `?lob=${encodeURIComponent(lobId)}` : '';
    return request<Analysis>('GET', `/api/admin/engagements/${encodeURIComponent(engagementId)}/analysis${query}`);
  },
  audit: () => request<{ entries: AuditEntry[] }>('GET', '/api/admin/audit').then((r) => r.entries),

  // onboarding
  createEngagement: (body: Record<string, unknown>) =>
    request<{ id: string }>('POST', '/api/admin/engagements', body),
  updateEngagement: (id: string, body: Record<string, unknown>) =>
    request<{ ok: true }>('PATCH', `/api/admin/engagements/${encodeURIComponent(id)}`, body),
  deleteEngagement: (id: string) =>
    request<{ ok: true }>('DELETE', `/api/admin/engagements/${encodeURIComponent(id)}`),
  getSurveyConfig: (id: string) =>
    request<SurveyConfig>('GET', `/api/admin/engagements/${encodeURIComponent(id)}/survey-config`),
  saveSurveyConfig: (id: string, excludedDimensionIds: string[], excludedQuestionIds: string[]) =>
    request<{ ok: true }>('PUT', `/api/admin/engagements/${encodeURIComponent(id)}/survey-config`, { excludedDimensionIds, excludedQuestionIds }),
  getCustomQuestions: (id: string) =>
    request<CustomQuestionsResponse>('GET', `/api/admin/engagements/${encodeURIComponent(id)}/custom-questions`),
  addCustomQuestion: (id: string, body: { dimensionId?: string; customDimensionId?: string; promptText: string; anchor1?: string; anchor2?: string; anchor3?: string; anchor4?: string; anchor5?: string }) =>
    request<{ id: string }>('POST', `/api/admin/engagements/${encodeURIComponent(id)}/custom-questions`, body),
  updateCustomQuestion: (id: string, qid: string, body: Record<string, unknown>) =>
    request<{ ok: true }>('PUT', `/api/admin/engagements/${encodeURIComponent(id)}/custom-questions/${encodeURIComponent(qid)}`, body),
  deleteCustomQuestion: (id: string, qid: string) =>
    request<{ ok: true }>('DELETE', `/api/admin/engagements/${encodeURIComponent(id)}/custom-questions/${encodeURIComponent(qid)}`),
  addCustomDimension: (id: string, name: string) =>
    request<{ id: string }>('POST', `/api/admin/engagements/${encodeURIComponent(id)}/custom-dimensions`, { name }),
  deleteCustomDimension: (id: string, did: string) =>
    request<{ ok: true }>('DELETE', `/api/admin/engagements/${encodeURIComponent(id)}/custom-dimensions/${encodeURIComponent(did)}`),
  setCustomDimensionActive: (id: string, did: string, isActive: boolean) =>
    request<{ ok: true }>('PUT', `/api/admin/engagements/${encodeURIComponent(id)}/custom-dimensions/${encodeURIComponent(did)}/active`, { isActive }),

  // Lightweight-assessment question library (Data Questions / Department-Specific
  // Questions) — global, shared by every engagement of that assessment type, not
  // scoped to a single engagement like custom questions.
  getLibraryQuestions: () => request<LibraryQuestionsResponse>('GET', '/api/admin/library-questions'),
  updateLibraryQuestion: (id: string, body: { promptText?: string; subCategory?: string; anchors?: LibraryAnchor[] }) =>
    request<{ ok: true }>('PUT', `/api/admin/library-questions/${encodeURIComponent(id)}`, body),
  deleteLibraryQuestion: (id: string) =>
    request<{ ok: true }>('DELETE', `/api/admin/library-questions/${encodeURIComponent(id)}`),
  restoreLibraryQuestion: (id: string) =>
    request<{ ok: true }>('POST', `/api/admin/library-questions/${encodeURIComponent(id)}/restore`),
  listLobs: (id: string) =>
    request<{ lobs: Lob[] }>('GET', `/api/admin/engagements/${encodeURIComponent(id)}/lobs`).then((r) => r.lobs),
  createLobs: (id: string, names: string[]) =>
    request<{ lobs: Lob[] }>('POST', `/api/admin/engagements/${encodeURIComponent(id)}/lobs`, { names }).then((r) => r.lobs),
  validateRoster: (id: string, rows: RosterInputRow[]) =>
    request<RosterValidation>('POST', `/api/admin/engagements/${encodeURIComponent(id)}/respondents/validate`, { rows }),
  provisionRespondents: (id: string, rows: RosterInputRow[]) =>
    request<ProvisionResult>('POST', `/api/admin/engagements/${encodeURIComponent(id)}/respondents`, { rows }),
  listRespondents: (id: string) =>
    request<RespondentMonitoring>('GET', `/api/admin/engagements/${encodeURIComponent(id)}/respondents`),
  regenerateCredentials: (id: string, rid: string) =>
    request<{ username: string; password: string }>('POST', `/api/admin/engagements/${encodeURIComponent(id)}/respondents/${encodeURIComponent(rid)}/regenerate`),

  // Full Excel workbook (per-user answers + analysis). Fetched as a Blob (not JSON) and
  // saved via a temporary anchor, mirroring downloadCsv in RespondentsView.
  downloadExport: async (id: string) => {
    const res = await fetch(`/api/admin/engagements/${encodeURIComponent(id)}/export.xlsx`, { credentials: 'include' });
    if (!res.ok) {
      const payload = (await res.json().catch(() => ({}))) as { message?: string; error?: string };
      throw new ApiError(res.status, payload.message ?? payload.error ?? res.statusText, payload.error);
    }
    const disposition = res.headers.get('content-disposition') ?? '';
    const filename = /filename="?([^"]+)"?/.exec(disposition)?.[1] ?? 'dma-export.xlsx';
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};
